// Notifikace: kanály a tiché hodiny (GET/PATCH /v1/me/notifications) a zařízení s push.
// Chyby se vracejí jako klíč i18n, překládá je až view.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  DEFAULT_NOTIFICATION_PREFS,
  type DigestTestResponse,
  type NotificationPrefs,
  type PushDevice,
  type UpdateNotificationPrefsInput,
} from '@wac-buddy/shared'
import {
  createPushSubscription,
  deletePushSubscription,
  getNotificationPrefs,
  getVapidKey,
  listPushDevices,
  sendDigestTest as sendDigestTestRequest,
  updateNotificationPrefs,
} from '@/api/notifications'
import { ApiClientError } from '@/api/client'
import {
  getNativePermission,
  getPermission,
  isPushSupported,
  registerNativePush,
  requestPermission,
  subscribePush,
  unregisterNativePush,
  unsubscribePush,
  type PushPermission,
} from '@/lib/push'
import { isNative, nativePlatform } from '@/lib/native'

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error'
export type ActionResult = { ok: true } | { ok: false; errorKey: string }
export type DigestTestResult =
  { ok: true; result: DigestTestResponse } | { ok: false; errorKey: string }

/** Stav push na tomto zařízení (řídí texty a tlačítko v Nastavení). */
export type PushState = 'unsupported' | 'server_off' | 'denied' | 'enabled' | 'available'

const ok: ActionResult = { ok: true }
const fail = (errorKey: string): ActionResult => ({ ok: false, errorKey })

function errorKey(e: unknown, generic: string): string {
  return e instanceof ApiClientError ? generic : 'login.errors.network'
}

function clonePrefs(p: NotificationPrefs): NotificationPrefs {
  return { channels: { ...p.channels }, quietFrom: p.quietFrom, quietTo: p.quietTo }
}

export const useNotificationsStore = defineStore('notifications', () => {
  const prefs = ref<NotificationPrefs>(clonePrefs(DEFAULT_NOTIFICATION_PREFS))
  const prefsStatus = ref<LoadStatus>('idle')
  const saving = ref(false)
  const digestSending = ref(false)

  const devices = ref<PushDevice[]>([])
  const devicesStatus = ref<LoadStatus>('idle')
  /** null = server má push vypnutý (chybí VAPID klíče), undefined = ještě nenačteno. */
  const vapidPublicKey = ref<string | null | undefined>(undefined)
  const permission = ref<PushPermission>(getPermission())
  const supported = ref(isPushSupported())
  const busy = ref(false)
  /** Zařízení založené v této session (server může `current` určit až podle endpointu). */
  const localDeviceId = ref<string | null>(null)

  const currentDevice = computed(
    () => devices.value.find((d) => d.current || d.id === localDeviceId.value) ?? null,
  )

  const pushState = computed<PushState>(() => {
    if (!supported.value) return 'unsupported'
    // VAPID je jen webová cesta; nativní push jde přes FCM/APNs a klíč nepotřebuje.
    if (!isNative() && vapidPublicKey.value === null) return 'server_off'
    if (permission.value === 'denied') return 'denied'
    if (currentDevice.value && permission.value === 'granted') return 'enabled'
    return 'available'
  })

  function refreshBrowserState() {
    supported.value = isPushSupported()
    // Nativní oprávnění se čte asynchronně (syncNativePush), tady by ho přepsal výchozí stav.
    if (!isNative()) permission.value = getPermission()
  }

  async function loadPrefs(): Promise<void> {
    prefsStatus.value = 'loading'
    try {
      prefs.value = clonePrefs(await getNotificationPrefs())
      prefsStatus.value = 'ready'
    } catch {
      prefsStatus.value = 'error'
    }
  }

  /** Uloží částečnou změnu (PATCH) a převezme odpověď serveru. */
  async function savePrefs(patch: UpdateNotificationPrefsInput): Promise<ActionResult> {
    saving.value = true
    try {
      prefs.value = clonePrefs(await updateNotificationPrefs(patch))
      return ok
    } catch (e) {
      return fail(errorKey(e, 'settings.notifications.errors.save'))
    } finally {
      saving.value = false
    }
  }

  /** Pošle přihlášenému uživateli ranní přehled hned (bez dedupe, nezasahuje do ranního běhu). */
  async function sendDigestTest(): Promise<DigestTestResult> {
    digestSending.value = true
    try {
      return { ok: true, result: await sendDigestTestRequest() }
    } catch (e) {
      return { ok: false, errorKey: errorKey(e, 'settings.notifications.digestTest.error') }
    } finally {
      digestSending.value = false
    }
  }

  async function loadDevices(): Promise<void> {
    refreshBrowserState()
    devicesStatus.value = 'loading'
    try {
      const [vapid, list] = await Promise.all([getVapidKey(), listPushDevices()])
      vapidPublicKey.value = vapid.publicKey
      devices.value = list
      devicesStatus.value = 'ready'
    } catch {
      devicesStatus.value = 'error'
      return
    }
    if (isNative()) await syncNativePush()
  }

  /** Uloží token tohoto telefonu na server a zařadí zařízení do seznamu. */
  async function saveNativeToken(token: string): Promise<void> {
    const platform = nativePlatform()
    if (platform === 'web') throw new Error('Není nativní platforma.')
    const device = await createPushSubscription({
      platform,
      token,
      userAgent: navigator.userAgent.slice(0, 300),
    })
    localDeviceId.value = device.id
    devices.value = [device, ...devices.value.filter((d) => d.id !== device.id)]
  }

  /**
   * Nativně po startu a po přihlášení: když je oprávnění už uděleno, tiše znovu zaregistruje zařízení
   * (token FCM se mění) a uloží ho na server. Na web a bez oprávnění nedělá nic, nikoho se neptá.
   */
  async function syncNativePush(): Promise<void> {
    if (!isNative()) return
    permission.value = await getNativePermission()
    if (permission.value !== 'granted') return
    const res = await registerNativePush({ prompt: false })
    if (!res.ok) return
    try {
      await saveNativeToken(res.token)
    } catch {
      /* zkusí se při příštím startu nebo ručně v Nastavení */
    }
  }

  /** Nativní zapnutí: oprávnění, registrace u FCM/APNs, uložení tokenu. */
  async function enableNativePush(): Promise<ActionResult> {
    busy.value = true
    try {
      const res = await registerNativePush({ prompt: true })
      if (!res.ok) {
        if (res.reason === 'denied') {
          permission.value = 'denied'
          return fail('native.push.errors.denied')
        }
        return fail(
          res.reason === 'dismissed' ? 'push.errors.dismissed' : 'native.push.errors.register',
        )
      }
      permission.value = 'granted'
      try {
        await saveNativeToken(res.token)
      } catch (e) {
        return fail(errorKey(e, 'push.errors.save'))
      }
      return ok
    } finally {
      busy.value = false
    }
  }

  /** Zapne push na tomto zařízení: oprávnění, odběr v prohlížeči, uložení na server. */
  async function enablePush(): Promise<ActionResult> {
    if (isNative()) return enableNativePush()
    refreshBrowserState()
    if (!supported.value) return fail('push.errors.unsupported')
    const key = vapidPublicKey.value
    if (!key) return fail('push.errors.serverOff')
    if (permission.value === 'denied') return fail('push.errors.denied')

    busy.value = true
    try {
      if (permission.value !== 'granted') {
        permission.value = await requestPermission()
        if (permission.value !== 'granted') {
          return fail(
            permission.value === 'denied' ? 'push.errors.denied' : 'push.errors.dismissed',
          )
        }
      }
      let sub
      try {
        sub = await subscribePush(key)
      } catch {
        return fail('push.errors.subscribe')
      }
      try {
        const device = await createPushSubscription({
          platform: 'web',
          endpoint: sub.endpoint,
          keys: sub.keys,
          userAgent: navigator.userAgent.slice(0, 300),
        })
        localDeviceId.value = device.id
        devices.value = [device, ...devices.value.filter((d) => d.id !== device.id)]
      } catch (e) {
        // Odběr bez záznamu na serveru by nic nedoručil, tak ho zase zrušíme.
        await unsubscribePush().catch(() => false)
        return fail(errorKey(e, 'push.errors.save'))
      }
      return ok
    } finally {
      busy.value = false
    }
  }

  /** Odebere zařízení. Je-li to toto zařízení, zruší i odběr v prohlížeči. */
  async function removeDevice(id: string): Promise<ActionResult> {
    const target = devices.value.find((d) => d.id === id)
    busy.value = true
    try {
      if (target && (target.current || target.id === localDeviceId.value)) {
        await unsubscribePush().catch(() => false)
        await unregisterNativePush()
        localDeviceId.value = null
      }
      try {
        await deletePushSubscription(id)
      } catch (e) {
        return fail(errorKey(e, 'push.errors.remove'))
      }
      devices.value = devices.value.filter((d) => d.id !== id)
      return ok
    } finally {
      busy.value = false
    }
  }

  return {
    prefs,
    prefsStatus,
    saving,
    digestSending,
    devices,
    devicesStatus,
    vapidPublicKey,
    permission,
    supported,
    busy,
    currentDevice,
    pushState,
    loadPrefs,
    savePrefs,
    sendDigestTest,
    loadDevices,
    syncNativePush,
    enablePush,
    removeDevice,
  }
})
